/**
 * VIVANA × IT GEEKS — Subscription Cart Manager
 *
 * Automatically adds the correct number of DOI Pack (Refill Doypack) line items
 * to the first order when a Quarterly or Annual Appstle subscription plan is
 * chosen, keeps their quantity in step with the subscription, cleans them up if
 * the subscription is removed, and surfaces a "what's included" notice on the PDP.
 *
 * DESIGN NOTE — why a post-add reconciler instead of a form interceptor:
 * The Horizon theme handles add-to-cart through a single document-level,
 * capture-phase delegated `submit` listener (assets/component.js → on:submit).
 * Racing that listener is brittle and risks breaking checkout. Instead this
 * module lets the theme's native flow add the subscription jar (+ Appstle selling
 * plan) and then hooks the theme's own `cart:update` event to reconcile the DOI
 * packs, re-rendering through the same native event. Zero changes to the core
 * add-to-cart path → upgrade-safe and non-breaking.
 *
 * Dependencies: vivana-doi-config.js (loaded first), @theme/events (importmap).
 * Compatible with: Shopify Horizon, Appstle Subscriptions.
 */

import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const CONFIG = window.VIVANA_SUBSCRIPTION_CONFIG;
const SOURCE = 'vivana-subscription-cart';
const RECONCILE_DEBOUNCE_MS = 50;

// Verbose logging: enable with ?vivana_debug=1 in the URL, localStorage
// vivana_debug=1, or features.debug=true in the config.
const DEBUG = (() => {
  try {
    if (new URLSearchParams(location.search).get('vivana_debug') === '1') return true;
    if (localStorage.getItem('vivana_debug') === '1') return true;
  } catch (e) {
    /* ignore */
  }
  return CONFIG?.features?.debug === true;
})();

function log(...args) {
  if (DEBUG) console.log('[Vivana]', ...args);
}

// NOTE: the module kickoff (installAddInterceptor + init) lives at the BOTTOM of
// this file. Because this is a deferred `type="module"` script, the DOM is usually
// already parsed when it runs, so init() calls start() synchronously DURING module
// evaluation. start() references `const` singletons (BundleResolver, CartAPI, …)
// declared further down — kicking off from the top would hit them in their temporal
// dead zone and throw. Running last guarantees every declaration is initialized.

function init() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}

function start() {
  // React to every cart change in the theme (adds, qty changes, removals).
  document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);
  // PDP "what's included" notice.
  bindPlanNotice();
  // Store-wide quantity cap "contact us" message (REQ 5).
  bindQuantityCapMessages();
  // Instant (pre-network) doypack quantity display when a jar qty changes.
  bindInstantDoypackDisplay();
  // Pre-resolve bundle products so the add interceptor can swap synchronously
  // (no main-product flicker). The server-injected map (theme.liquid) is the
  // reliable source for hidden/UNLISTED products; the URL fetch is a fallback.
  if (CONFIG.subscriptionBundleSwap?.enabled) {
    BundleResolver.prewarmFromMap();
    BundleResolver.prewarmFromUrl();
  }
}

/**
 * Updates the linked doypack's displayed quantity the instant a jar quantity
 * control is used — before the theme's debounce and the server round-trip — so
 * the number changes in microseconds. The background reconcile then persists it.
 */
function bindInstantDoypackDisplay() {
  const sync = (component) => {
    if (!component || component.closest('.cart-items__table-row--doi-locked')) return;
    const variantId = component.getAttribute('data-variant-id');
    const input = component.querySelector('input[type="number"]');
    if (!variantId || !input) return;
    const jarQty = parseInt(input.value, 10) || 0;
    document
      .querySelectorAll(`[data-vivana-doi-linked="${cssEscape(variantId)}"]`)
      .forEach((row) => {
        const tier = row.getAttribute('data-vivana-doi-tier');
        const expected = (CONFIG.doiPackQuantityMap[tier] || 0) * jarQty;
        row
          .querySelectorAll('[data-vivana-doi-qty-value]')
          .forEach((el) => (el.textContent = String(expected)));
      });
  };

  // Typing in / blurring the input.
  const onFieldEvent = (e) => {
    if (e.target instanceof HTMLInputElement && e.target.type === 'number') {
      sync(e.target.closest('cart-quantity-selector-component'));
    }
  };
  document.addEventListener('input', onFieldEvent);
  document.addEventListener('change', onFieldEvent);
  // +/- buttons set the value programmatically (no input event) — read after.
  document.addEventListener('click', (e) => {
    const btn = e.target instanceof Element ? e.target.closest('.quantity-plus, .quantity-minus') : null;
    if (btn) {
      const component = btn.closest('cart-quantity-selector-component');
      requestAnimationFrame(() => sync(component));
    }
  });
}

/* ───────────────────────────── Cart API ───────────────────────────── */

const CartAPI = {
  async fetch() {
    // Cache-bust + no-store so we never reconcile against a stale cart snapshot
    // (a cached /cart.js would make the qty sync-down silently no-op).
    const base = `${Theme.routes.cart_url || ''}.js`;
    const url = `${base}${base.includes('?') ? '&' : '?'}t=${Date.now()}`;
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store',
    });
    if (!res.ok) throw new Error(`cart fetch ${res.status}`);
    return res.json();
  },

  async add(items, sections) {
    const body = { items };
    if (sections && sections.length) {
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }
    const res = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || `cart add ${res.status}`);
    }
    return res.json();
  },

  async change(key, quantity, sections) {
    const body = { id: key, quantity };
    if (sections && sections.length) {
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }
    const res = await fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || `cart change ${res.status}`);
    }
    return res.json();
  },
};

/** Section ids the theme uses to render the cart (drawer + cart page). */
function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

/* ───────────────────────── Plan classification ─────────────────────── */

const PlanClassifier = {
  /**
   * @param {string|number|null} planId
   * @param {string} [planName]
   * @returns {('monthly'|'quarterly'|'annual'|null)}
   */
  getTier(planId, planName) {
    if (planId != null && CONFIG.sellingPlanTierMap[String(planId)]) {
      return CONFIG.sellingPlanTierMap[String(planId)];
    }
    const haystack = String(planName || '').toLowerCase();
    if (haystack) {
      for (const { tier, keywords } of CONFIG.tierNameKeywords || []) {
        if (keywords.some((kw) => haystack.includes(kw.toLowerCase()))) return tier;
      }
    }
    return null;
  },

  requiresDoiPacks(tier) {
    return (CONFIG.doiPackQuantityMap[tier] || 0) > 0;
  },

  doiQuantity(tier, mainQuantity = 1) {
    return (CONFIG.doiPackQuantityMap[tier] || 0) * mainQuantity;
  },
};

/* ───────────────────────── DOI variant resolver ───────────────────── */

const DoiVariantResolver = {
  _cache: {},

  async resolve(productId, handle) {
    const cacheKey = String(productId || handle || '');
    if (this._cache[cacheKey]) return this._cache[cacheKey];

    // 1. Explicit config map.
    const mapped = CONFIG.productDoiVariantMap[String(productId)];
    if (mapped) return (this._cache[cacheKey] = Number(mapped));

    // 2. Liquid-injected data attribute on the product form (PDP only).
    const form = document.querySelector(CONFIG.selectors.productForm);
    const attr = form?.dataset?.vivanaDoiVariantId;
    if (attr) return (this._cache[cacheKey] = Number(attr));

    // 3. Resolve from product JSON by variant title/option.
    if (handle) {
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (res.ok) {
          const product = await res.json();
          const keywords = CONFIG.doiVariantNameKeywords || ['doypack', 'refill'];
          const match = product.variants.find((v) => {
            const blob = [v.title, v.option1, v.option2, v.option3]
              .filter(Boolean)
              .join(' ')
              .toLowerCase();
            return keywords.some((kw) => blob.includes(String(kw).toLowerCase()));
          });
          if (match) return (this._cache[cacheKey] = match.id);
        }
      } catch (e) {
        console.warn('[Vivana] DOI variant lookup failed for', handle, e);
      }
    }
    return null;
  },
};

/* ─────────────── Bundle resolver (handle-based matching) ───────────── */

/**
 * Resolves a MAIN product to its hidden BUNDLE/subscription product purely from
 * the product HANDLE — no per-product name pairing or variant/plan mapping.
 *
 * Convention: the subscription product lives at `${mainHandle}${handleSuffix}`
 * (default suffix `-subscription`). Its variant id and per-tier selling-plan ids
 * are read LIVE from `/products/<sub-handle>.js`, so any future product works
 * automatically as long as it follows the convention.
 *
 * Async resolution feeds the post-add reconciler; a sync cache (populated by the
 * PDP pre-warm) feeds the in-flight /cart/add interceptor so the swap is
 * flicker-free. A cache miss simply falls back to the reconciler safety net.
 */
const BundleResolver = {
  _byHandle: {}, // mainHandle -> Promise<resolved|null> (in-flight de-dupe)
  _sync: {}, // mainHandle -> resolved|null (sync-readable once resolved)
  _variantIndex: {}, // mainVariantId(string) -> mainHandle (pre-warmed on PDP)

  _cfg() {
    return CONFIG.subscriptionBundleSwap || {};
  },
  suffix() {
    return this._cfg().handleSuffix || '-subscription';
  },
  isSubscriptionHandle(handle) {
    return !!handle && String(handle).endsWith(this.suffix());
  },
  subscriptionHandleFor(mainHandle) {
    if (!mainHandle || this.isSubscriptionHandle(mainHandle)) return null;
    return `${mainHandle}${this.suffix()}`;
  },

  /**
   * Build a resolved bundle from the server-side injected map (theme.liquid), or
   * null. This is the PRIMARY source — it works for UNLISTED/hidden-redirect
   * subscription products whose storefront `/products/<handle>.js` can't be relied on.
   */
  _fromMap(mainHandle) {
    const entry = (this._cfg().bundleMap || {})[mainHandle];
    if (!entry || !entry.variantId) return null;
    const plansByTier = {};
    for (const plan of entry.plans || []) {
      const tier = PlanClassifier.getTier(plan.id, plan.name);
      if (tier && plansByTier[tier] == null) plansByTier[tier] = plan.id;
    }
    return {
      bundleVariantId: Number(entry.variantId),
      plansByTier,
      mainUrl: entry.mainUrl || `/products/${mainHandle}`,
      subHandle: entry.subHandle,
    };
  },

  /** Async resolve (cached). Returns { bundleVariantId, plansByTier, mainUrl } or null. */
  async resolveByHandle(mainHandle) {
    if (this._cfg().autoMatchByHandle === false) return null;
    if (mainHandle in this._sync) return this._sync[mainHandle];
    if (this._byHandle[mainHandle]) return this._byHandle[mainHandle];

    // 1. Server-side injected map (preferred — robust for hidden products).
    const fromMap = this._fromMap(mainHandle);
    if (fromMap) return (this._sync[mainHandle] = fromMap);

    // 2. Fallback: storefront product JSON (only works for accessible products).
    const subHandle = this.subscriptionHandleFor(mainHandle);
    if (!subHandle) return (this._sync[mainHandle] = null);

    this._byHandle[mainHandle] = (async () => {
      try {
        const res = await fetch(`/products/${encodeURIComponent(subHandle)}.js`, {
          cache: 'no-store',
        });
        if (!res.ok) return (this._sync[mainHandle] = null); // no subscription product
        const product = await res.json();
        const variants = product.variants || [];
        const variant = variants.find((v) => v.available) || variants[0];
        if (!variant) return (this._sync[mainHandle] = null);

        // Map each of the bundle's own selling plans to a tier (first wins).
        const plansByTier = {};
        for (const group of product.selling_plan_groups || []) {
          for (const plan of group.selling_plans || []) {
            const tier = PlanClassifier.getTier(plan.id, plan.name);
            if (tier && plansByTier[tier] == null) plansByTier[tier] = plan.id;
          }
        }
        const resolved = {
          bundleVariantId: Number(variant.id),
          plansByTier,
          mainUrl: `/products/${mainHandle}`,
          subHandle,
        };
        log('bundle resolved', mainHandle, '→', subHandle, resolved);
        return (this._sync[mainHandle] = resolved);
      } catch (e) {
        console.warn('[Vivana] bundle resolve failed for', subHandle, e);
        return (this._sync[mainHandle] = null);
      }
    })();
    return this._byHandle[mainHandle];
  },

  /**
   * Builds a tier-specific swap target from a resolved bundle, or null. Only
   * DOI-bearing tiers (quarterly/annual) swap; monthly/one-time stay on the main
   * product even though the bundle may also offer a monthly plan.
   */
  targetForTier(resolved, tier) {
    if (!resolved || !tier) return null;
    if ((CONFIG.doiPackQuantityMap?.[tier] || 0) <= 0) return null;
    const planId = resolved.plansByTier?.[tier];
    if (!planId) return null; // tier not offered on the bundle
    return {
      bundleVariantId: Number(resolved.bundleVariantId),
      bundleSellingPlanId: Number(planId),
      tier,
      mainUrl: resolved.mainUrl || '',
    };
  },

  /** Synchronous lookup for the fetch interceptor (requires PDP pre-warm). */
  syncTargetForVariant(variantId, tier) {
    const handle = this._variantIndex[String(variantId)];
    if (!handle) return null;
    const resolved = this._sync[handle];
    if (resolved === undefined) return null; // not warmed yet → reconciler covers it
    return this.targetForTier(resolved, tier);
  },

  /**
   * Seed the sync cache + variant index from the server-injected map (no network).
   * This is what makes the in-flight add interceptor swap reliably on the PDP,
   * including for hidden/UNLISTED subscription products.
   */
  prewarmFromMap() {
    if (this._cfg().enabled !== true || this._cfg().autoMatchByHandle === false) return;
    const map = this._cfg().bundleMap || {};
    for (const mainHandle in map) {
      const resolved = this._fromMap(mainHandle);
      this._sync[mainHandle] = resolved;
      if (resolved) {
        for (const vid of map[mainHandle].mainVariantIds || []) {
          this._variantIndex[String(vid)] = mainHandle;
        }
      }
    }
  },

  /** Fetch-based pre-warm fallback for a PDP not covered by the injected map. */
  async prewarmFromUrl() {
    if (this._cfg().enabled !== true || this._cfg().autoMatchByHandle === false) return;
    const m = String(location.pathname || '').match(/\/products\/([^/?#]+)/);
    const handle = m && decodeURIComponent(m[1]);
    if (!handle || this.isSubscriptionHandle(handle)) return;
    if (handle in this._sync && this._sync[handle]) return; // already covered by the injected map
    if (!this.subscriptionHandleFor(handle)) return;
    await Promise.all([this.resolveByHandle(handle), this._indexMainVariants(handle)]);
  },

  /** Index the main product's variant ids → its handle for the sync interceptor. */
  async _indexMainVariants(mainHandle) {
    try {
      const res = await fetch(`/products/${encodeURIComponent(mainHandle)}.js`, {
        cache: 'no-store',
      });
      if (!res.ok) return;
      const product = await res.json();
      for (const v of product.variants || []) this._variantIndex[String(v.id)] = mainHandle;
    } catch (e) {
      /* non-fatal: the reconciler still swaps post-add */
    }
  },
};

/* ──────────────────────────── Reconciler ──────────────────────────── */

let reconciling = false;
let reconcileQueued = false;
let debounceTimer = null;

function onCartUpdate(event) {
  // Ignore the events we dispatch ourselves to avoid redundant passes.
  if (event?.detail?.data?.source === SOURCE) return;
  if (
    !CONFIG.features.autoAddDoiPacks &&
    !CONFIG.features.syncDoiOnQtyChange &&
    !CONFIG.subscriptionBundleSwap?.enabled
  ) {
    return;
  }

  // The theme attaches the freshly-updated cart to its event. Use it for an
  // instant visual update and to skip an extra /cart.js round-trip.
  const hint = event?.detail?.resource;

  // Instant: paint the correct doypack quantity on the next frame, before the
  // background persistence round-trip completes.
  if (hint && Array.isArray(hint.items)) {
    requestAnimationFrame(() => applyOptimisticDoypackQty(hint));
  }

  log('cart:update received from', event?.detail?.data?.source || 'unknown', '→ scheduling reconcile');
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => reconcile(hint), RECONCILE_DEBOUNCE_MS);
}

/**
 * Optimistic UI: immediately set each locked doypack line's displayed quantity to
 * tier × jar quantity, without waiting for the server. The background reconcile
 * then persists the same value, so there is no flicker.
 * @param {{items: any[]}} cart
 */
function applyOptimisticDoypackQty(cart) {
  const P = CONFIG.properties;
  const items = cart.items || [];
  const doiLines = items.filter((it) => it.properties?.[P.isDoiPack] === 'true');
  if (!doiLines.length) return;

  for (const sub of items) {
    if (sub.properties?.[P.isDoiPack] === 'true') continue;
    const plan = sub.selling_plan_allocation?.selling_plan;
    if (!plan) continue;
    const tier = PlanClassifier.getTier(plan.id, plan.name);
    if (!PlanClassifier.requiresDoiPacks(tier)) continue;

    const expected = PlanClassifier.doiQuantity(tier, sub.quantity);
    const linked = doiLines.find((d) => d.properties?.[P.linkedVariant] === String(sub.variant_id));
    if (!linked) continue;

    document
      .querySelectorAll(`[data-key="${cssEscape(linked.key)}"] [data-vivana-doi-qty-value]`)
      .forEach((el) => {
        el.textContent = String(expected);
      });
  }
}

function cssEscape(value) {
  if (window.CSS && typeof CSS.escape === 'function') return CSS.escape(value);
  return String(value).replace(/["\\]/g, '\\$&');
}

/**
 * Single source of truth: ensures every quarterly/annual subscription line has a
 * correctly-sized linked DOI pack, and removes orphaned DOI packs. Naturally
 * terminating — a follow-up pass finds nothing to change and stops.
 */
async function reconcile(hint) {
  if (reconciling) {
    reconcileQueued = true;
    return;
  }
  reconciling = true;

  try {
    const P = CONFIG.properties;
    const sectionIds = cartSectionIds();
    let cart = hint && Array.isArray(hint.items) ? hint : await CartAPI.fetch();
    let items = cart.items || [];
    let changed = false;
    let lastMutationFullCart = false; // true when the last mutation returned a full cart + sections
    let lastChange = null; // last /cart/change response (full cart + rendered sections)
    let lastSections = null; // rendered sections from the last mutation (avoids a render fetch)

    // 0. Store-wide cap safety net (REQ 4). Clamp any customer line above the cap
    //    BEFORE sizing doypacks, so the 2x rule uses corrected jar quantities.
    //    The managed doypack line is excluded (it legitimately holds tier x jar).
    const cap = Number(CONFIG.maxQuantity) || 0;
    if (CONFIG.features.enforceMaxInReconciler && cap > 0) {
      let clamped = false;
      for (const it of items) {
        if (it.properties?.[P.isDoiPack] === 'true') continue;
        if (it.quantity > cap) {
          log(`cap: line ${it.variant_id} ${it.quantity} → ${cap}`);
          lastChange = await CartAPI.change(it.key, cap, sectionIds);
          lastSections = lastChange.sections || lastSections;
          lastMutationFullCart = true;
          clamped = true;
          changed = true;
        }
      }
      if (clamped && lastChange) {
        items = lastChange.items || items;
      }
    }

    // 0.5 Subscription bundle swap (context.md §F). When a configured MAIN line is
    //     on a quarterly/annual plan, replace it with the hidden BUNDLE product so
    //     only one bundle line is shown. Runs before DOI sizing so the (self-
    //     contained) bundle line is in place and never gets separate doypacks.
    if (CONFIG.subscriptionBundleSwap?.enabled) {
      const swapKeys = [];
      const bundleAdds = [];
      for (const it of items) {
        if (it.properties?.[P.bundleSwap] === 'true') continue; // already a bundle line
        const target = await bundleSwapTarget(it);
        if (!target) continue;
        const mainUrl = (it.url || '').split('?')[0]; // main product page, no variant query
        log(`bundle-swap: main ${it.variant_id} → bundle ${target.bundleVariantId} (${target.tier}) ×${it.quantity}`);
        bundleAdds.push(
          buildBundleItem(target.bundleVariantId, it.quantity, it.variant_id, target.tier, target.bundleSellingPlanId, mainUrl)
        );
        swapKeys.push(it.key);
      }
      if (bundleAdds.length) {
        // Two round-trips instead of four: add all bundle lines in one request
        // (no section render needed yet), then remove the main lines — requesting
        // rendered sections only on the LAST removal so its response is the full
        // updated cart + drawer HTML, reused directly for the re-render below.
        await CartAPI.add(bundleAdds);
        lastMutationFullCart = false;
        for (let i = 0; i < swapKeys.length; i++) {
          const withSections = i === swapKeys.length - 1 ? sectionIds : undefined;
          lastChange = await CartAPI.change(swapKeys[i], 0, withSections);
        }
        lastSections = lastChange?.sections || lastSections;
        lastMutationFullCart = true;
        changed = true;
        // Update the local snapshot from the change response (no extra /cart.js
        // fetch); the bundle line is DOI-excluded so it needn't appear here.
        items = lastChange?.items || items.filter((x) => !swapKeys.includes(x.key));
      }
    }

    const subscriptionLines = items.filter((it) => {
      // A DOI pack now carries the jar's selling plan too — never treat it as a
      // subscription line that itself needs DOI packs.
      if (it.properties?.[P.isDoiPack] === 'true') return false;
      // A swapped-in bundle is self-contained (1 jar + 2 doypacks) — never add
      // separate doypack lines for it.
      if (it.properties?.[P.bundleSwap] === 'true') return false;
      const plan = it.selling_plan_allocation?.selling_plan;
      if (!plan) return false;
      return PlanClassifier.requiresDoiPacks(PlanClassifier.getTier(plan.id, plan.name));
    });

    const doiLines = items.filter((it) => it.properties?.[P.isDoiPack] === 'true');

    log(
      'reconcile:',
      subscriptionLines.map((s) => `sub variant ${s.variant_id} ×${s.quantity}`),
      doiLines.map(
        (d) => `doi variant ${d.variant_id} ×${d.quantity} linked→${d.properties?.[P.linkedVariant]}`
      )
    );

    // 1. Ensure / size DOI packs for each qualifying subscription line. DOI packs
    //    carry the SAME selling plan as the jar, so they inherit the same discount
    //    and renew with the subscription.
    for (const sub of subscriptionLines) {
      const plan = sub.selling_plan_allocation.selling_plan;
      const tier = PlanClassifier.getTier(plan.id, plan.name);
      const expectedQty = PlanClassifier.doiQuantity(tier, sub.quantity);
      const linked = doiLines.find(
        (d) => d.properties?.[P.linkedVariant] === String(sub.variant_id)
      );

      if (linked) {
        const linkedPlanId = linked.selling_plan_allocation?.selling_plan?.id ?? null;
        const planMismatch = Number(linkedPlanId) !== Number(plan.id);

        if (planMismatch) {
          // A line's selling plan can't be changed in place — recreate it.
          log(`recreate: doi ${linked.variant_id} plan ${linkedPlanId} → ${plan.id}`);
          await CartAPI.change(linked.key, 0);
          {
            const addRes = await CartAPI.add(
              [buildDoiItem(linked.variant_id, expectedQty, sub.variant_id, tier, plan.id)],
              sectionIds
            );
            lastSections = addRes.sections || lastSections;
          }
          lastMutationFullCart = false;
          changed = true;
        } else if (CONFIG.features.syncDoiOnQtyChange && linked.quantity !== expectedQty) {
          log(`sync: doi ${linked.variant_id} ${linked.quantity} → ${expectedQty}`);
          lastChange = await CartAPI.change(linked.key, expectedQty, sectionIds);
          lastSections = lastChange.sections || lastSections;
          lastMutationFullCart = true;
          changed = true;
        } else {
          log(`ok: doi ${linked.variant_id} already ×${linked.quantity} (expected ${expectedQty})`);
        }
      } else if (CONFIG.features.autoAddDoiPacks) {
        const doiVariantId = await DoiVariantResolver.resolve(sub.product_id, sub.handle);
        log(`add: resolved doi variant ${doiVariantId} for sub ${sub.variant_id}, qty ${expectedQty}`);
        if (doiVariantId && Number(doiVariantId) !== sub.variant_id && expectedQty > 0) {
          const addRes = await CartAPI.add(
            [buildDoiItem(Number(doiVariantId), expectedQty, sub.variant_id, tier, plan.id)],
            sectionIds
          );
          lastSections = addRes.sections || lastSections;
          lastMutationFullCart = false;
          changed = true;
        }
      }
    }

    // 2. Remove orphaned DOI packs whose subscription line is gone.
    if (CONFIG.features.removeOrphanedDoiPacks) {
      const liveVariantIds = new Set(subscriptionLines.map((s) => String(s.variant_id)));
      for (const d of doiLines) {
        const link = d.properties?.[P.linkedVariant];
        if (link && !liveVariantIds.has(link)) {
          lastChange = await CartAPI.change(d.key, 0, sectionIds);
          lastSections = lastChange.sections || lastSections;
          lastMutationFullCart = true;
          changed = true;
        }
      }
    }

    // 3. Re-render through the theme's native cart-update pipeline. Fast path:
    //    when only quantity changes happened, the last /cart/change response is
    //    the final cart and already carries rendered sections — dispatch them so
    //    the theme morphs without any extra fetch.
    if (changed) {
      if (lastMutationFullCart && lastChange && lastChange.sections) {
        log('changed → dispatch with inline sections (fast path)');
        document.dispatchEvent(
          new CartUpdateEvent(lastChange, SOURCE, { source: SOURCE, sections: lastChange.sections })
        );
      } else {
        log('changed → dispatch with fetched resource + last sections');
        const fresh = await CartAPI.fetch();
        const data = lastSections ? { source: SOURCE, sections: lastSections } : { source: SOURCE };
        document.dispatchEvent(new CartUpdateEvent(fresh, SOURCE, data));
      }
    } else {
      log('no changes needed');
    }
  } catch (e) {
    console.warn('[Vivana] cart reconcile failed:', e);
  } finally {
    reconciling = false;
    if (reconcileQueued) {
      reconcileQueued = false;
      reconcile();
    }
  }
}

/**
 * Returns the bundle-swap target for a cart line, or null if it shouldn't swap.
 * Resolution order:
 *   1. Explicit override rule keyed by product id (optional).
 *   2. Dynamic handle match — the subscription product at `${handle}-subscription`.
 * Only quarterly/annual lines swap; one-time and monthly return null. A line that
 * is itself a subscription/bundle product (handle ends with the suffix) never swaps.
 * @returns {Promise<{bundleVariantId:number, bundleSellingPlanId:number, tier:string}|null>}
 */
async function bundleSwapTarget(item) {
  const plan = item.selling_plan_allocation?.selling_plan;
  if (!plan) return null;
  const tier = PlanClassifier.getTier(plan.id, plan.name);
  if (!tier) return null;

  // 1. Explicit override rule by product id.
  const rule = CONFIG.subscriptionBundleSwap?.rules?.[String(item.product_id)];
  if (rule) {
    const allowed = rule.mainVariantIds;
    if (
      Array.isArray(allowed) &&
      allowed.length &&
      !allowed.map(Number).includes(Number(item.variant_id))
    ) {
      return null;
    }
    const target = rule.tiers ? rule.tiers[tier] : null;
    if (!target || !target.bundleVariantId) return null;
    return {
      bundleVariantId: Number(target.bundleVariantId),
      bundleSellingPlanId: target.bundleSellingPlanId ? Number(target.bundleSellingPlanId) : null,
      tier,
    };
  }

  // 2. Dynamic handle-based match.
  if (BundleResolver.isSubscriptionHandle(item.handle)) return null;
  const resolved = await BundleResolver.resolveByHandle(item.handle);
  const target = BundleResolver.targetForTier(resolved, tier);
  if (!target) return null;
  return {
    bundleVariantId: target.bundleVariantId,
    bundleSellingPlanId: target.bundleSellingPlanId,
    tier,
  };
}

/** Builds a /cart/add item for the swapped-in bundle product. Underscore-prefixed
 *  properties are hidden from the customer; identical lines merge so quantity adds. */
function buildBundleItem(variantId, quantity, mainVariantId, tier, sellingPlanId, mainUrl) {
  const P = CONFIG.properties;
  const item = {
    id: Number(variantId),
    quantity: quantity || 1,
    properties: {
      [P.bundleSwap]: 'true',
      [P.bundleSource]: String(mainVariantId),
      [P.doiPlan]: tier,
    },
  };
  if (mainUrl) item.properties[P.bundleSourceUrl] = String(mainUrl);
  if (sellingPlanId) item.selling_plan = Number(sellingPlanId);
  return item;
}

/* ───────────────── Add-to-cart interception (fetch) ───────────────── */

/**
 * Patches window.fetch so a /cart/add for the MAIN product on a quarterly/annual
 * plan is rewritten in-flight to add the hidden BUNDLE instead — the theme then
 * renders the drawer once with the bundle (no main-product flicker, no swap flip).
 * Deterministic regardless of event-listener order; unmatched/unparseable bodies
 * pass through untouched and the post-add reconciler swap remains the safety net.
 * Also rewrites a plan-less add into a one-time pack of the bundle when the
 * pack selector has set window.VivanaPackState (see packTargetForKeyed).
 */
function installAddInterceptor() {
  const swapOn =
    CONFIG.subscriptionBundleSwap?.enabled === true &&
    CONFIG.subscriptionBundleSwap?.interceptAdd !== false;
  const packsOn = CONFIG.oneTimePacks?.enabled === true;
  if (!swapOn && !packsOn) return;
  if (typeof window.fetch !== 'function' || window.__vivanaFetchPatched) return;
  const orig = window.fetch;
  window.__vivanaFetchPatched = true;
  window.fetch = function (input, init) {
    try {
      const url = typeof input === 'string' ? input : input && input.url;
      if (init && init.body && typeof url === 'string' && /\/cart\/add(\.js)?(\?|$)/.test(url)) {
        const rewritten = rewriteAddBody(init.body);
        if (rewritten != null) {
          init = Object.assign({}, init, { body: rewritten });
          return orig.call(this, input, init);
        }
      }
    } catch (e) {
      /* never block a cart add on our account */
    }
    return orig.apply(this, arguments);
  };
  log('add interceptor installed');
}

/** Finds the bundle target for a (variant, plan) pair for the in-flight add. The
 *  add body carries no product id, so matching is by variant id:
 *    1. explicit override rules that list mainVariantIds, then
 *    2. the handle-based resolver's pre-warmed variant index.
 *  Returns null for one-time / monthly / not-yet-warmed adds (the post-add
 *  reconciler is the safety net for the latter). */
function swapTargetForVariant(variantId, planId) {
  // Only perform the in-flight subscription swap when that feature is on. The
  // install guard now also installs the interceptor for one-time packs alone,
  // so this must be re-checked here (packs use packTargetForKeyed instead).
  if (
    CONFIG.subscriptionBundleSwap?.enabled !== true ||
    CONFIG.subscriptionBundleSwap?.interceptAdd === false
  ) {
    return null;
  }
  if (!variantId || planId == null || planId === '') return null;
  const tier = PlanClassifier.getTier(planId);
  if (!tier) return null;

  // 1. Explicit override rules (only those listing mainVariantIds are matchable here).
  const rules = CONFIG.subscriptionBundleSwap?.rules || {};
  for (const pid in rules) {
    const rule = rules[pid];
    const allowed = rule.mainVariantIds;
    if (!(Array.isArray(allowed) && allowed.length && allowed.map(Number).includes(Number(variantId)))) continue;
    const t = rule.tiers ? rule.tiers[tier] : null;
    if (t && t.bundleVariantId) {
      return {
        bundleVariantId: Number(t.bundleVariantId),
        bundleSellingPlanId: t.bundleSellingPlanId ? Number(t.bundleSellingPlanId) : null,
        tier,
        mainUrl: rule.mainUrl || '',
      };
    }
  }

  // 2. Dynamic handle-based match (pre-warmed on the PDP).
  return BundleResolver.syncTargetForVariant(variantId, tier);
}

/** Rewrites a /cart/add body — subscription swap or one-time pack. Handles
 *  FormData (Horizon's product form), URLSearchParams and JSON. Returns the body to
 *  send, or null when nothing matched (send the original). */
function rewriteAddBody(body) {
  try {
    if (typeof FormData !== 'undefined' && body instanceof FormData) return rewriteKeyedBody(body);
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return rewriteKeyedBody(body);
    if (typeof body === 'string') return rewriteJsonAddBody(body);
  } catch (e) {
    /* fall through */
  }
  return null;
}

/** Mutates a FormData/URLSearchParams add body in place; returns it if changed. */
function rewriteKeyedBody(fd) {
  const P = CONFIG.properties;

  // 1. Subscription swap (quarterly/annual plan → hidden bundle + plan).
  const target = swapTargetForVariant(fd.get('id'), fd.get('selling_plan'));
  if (target) {
    const mainVariant = String(fd.get('id'));
    fd.set('id', String(target.bundleVariantId));
    if (target.bundleSellingPlanId) fd.set('selling_plan', String(target.bundleSellingPlanId));
    else fd.delete('selling_plan');
    fd.append(`properties[${P.bundleSwap}]`, 'true');
    fd.append(`properties[${P.bundleSource}]`, mainVariant);
    fd.append(`properties[${P.doiPlan}]`, target.tier);
    if (target.mainUrl) fd.append(`properties[${P.bundleSourceUrl}]`, target.mainUrl);
    log('intercept: rewrote add', mainVariant, '→ bundle', target.bundleVariantId);
    return fd;
  }

  // 2. One-time pack (no plan → hidden bundle, one-time, × multiplier, labeled).
  const pack = packTargetForKeyed(fd);
  if (pack) {
    const qty = window.VivanaPackLogic.packQuantity(pack, fd.get('quantity'));
    fd.set('id', String(pack.bundleVariantId));
    fd.delete('selling_plan');
    fd.set('quantity', String(qty));
    const props = window.VivanaPackLogic.packProperties(pack, P);
    for (const k in props) fd.append(`properties[${k}]`, props[k]);
    log('intercept: rewrote add', pack.mainVariantId, '→ pack', pack.bundleVariantId, '×', qty);
    return fd;
  }

  return null;
}

/** Pack target for a FormData/URLSearchParams add body, or null. */
function packTargetForKeyed(fd) {
  if (CONFIG.oneTimePacks?.enabled !== true) return null;
  if (!window.VivanaPackLogic) return null;
  return window.VivanaPackLogic.resolvePackTarget(
    fd.get('id'),
    fd.get('selling_plan'),
    window.VivanaPackState
  );
}

/** Rewrites a JSON add body; returns a new JSON string if changed, else null. */
function rewriteJsonAddBody(body) {
  let payload;
  try {
    payload = JSON.parse(body);
  } catch (e) {
    return null;
  }
  const single = !Array.isArray(payload.items);
  const items = single ? (payload.id != null ? [payload] : null) : payload.items;
  if (!items || !items.length) return null;

  const P = CONFIG.properties;
  let changed = false;
  const out = items.map((it) => {
    const target = swapTargetForVariant(it.id, it.selling_plan);
    if (!target) {
      // One-time pack fallback (no selling plan).
      if (CONFIG.oneTimePacks?.enabled === true && window.VivanaPackLogic) {
        const pack = window.VivanaPackLogic.resolvePackTarget(it.id, it.selling_plan, window.VivanaPackState);
        if (pack) {
          changed = true;
          const properties = Object.assign(
            {},
            it.properties,
            window.VivanaPackLogic.packProperties(pack, P)
          );
          return {
            id: pack.bundleVariantId,
            quantity: window.VivanaPackLogic.packQuantity(pack, it.quantity),
            properties: properties,
          };
        }
      }
      return it;
    }
    changed = true;
    const properties = Object.assign({}, it.properties, {
      [P.bundleSwap]: 'true',
      [P.bundleSource]: String(it.id),
      [P.doiPlan]: target.tier,
    });
    if (target.mainUrl) properties[P.bundleSourceUrl] = target.mainUrl;
    const next = { id: target.bundleVariantId, quantity: it.quantity || 1, properties };
    if (target.bundleSellingPlanId) next.selling_plan = target.bundleSellingPlanId;
    return next;
  });
  if (!changed) return null;

  if (single) {
    delete payload.id;
    delete payload.quantity;
    delete payload.selling_plan;
    delete payload.properties;
    Object.assign(payload, out[0]);
  } else {
    payload.items = out;
  }
  return JSON.stringify(payload);
}

/** Builds a /cart/add item for a DOI pack, carrying the jar's selling plan so it
 *  inherits the same discount and renews with the subscription. */
function buildDoiItem(variantId, quantity, linkedVariantId, tier, sellingPlanId) {
  const P = CONFIG.properties;
  const item = {
    id: Number(variantId),
    quantity,
    properties: {
      [P.isDoiPack]: 'true',
      [P.linkedVariant]: String(linkedVariantId),
      [P.doiPlan]: tier,
      [P.customerLabel]: CONFIG.customerLabelValue,
    },
  };
  if (sellingPlanId) item.selling_plan = Number(sellingPlanId);
  return item;
}

/* ─────────────────────── Quantity cap message (REQ 5) ─────────────── */

function bindQuantityCapMessages() {
  if (CONFIG.features.showQtyLimitMessage === false) return;
  if ((Number(CONFIG.maxQuantity) || 0) <= 0) return;

  const refresh = () => refreshQtyLimitMessages();

  // Quantity edits (typing, +/- buttons) and any cart re-render.
  document.addEventListener('input', (e) => {
    if (e.target instanceof HTMLInputElement && e.target.type === 'number') refresh();
  });
  document.addEventListener('change', (e) => {
    if (e.target instanceof HTMLInputElement && e.target.type === 'number') refresh();
  });
  document.addEventListener('click', (e) => {
    if (e.target instanceof Element && e.target.closest('.quantity-plus, .quantity-minus')) {
      // Run after the native component updates the input value.
      setTimeout(refresh, 0);
    }
  });
  // Section re-renders (drawer/cart morph) replace the inputs — re-evaluate.
  document.addEventListener(ThemeEvents.cartUpdate, () => setTimeout(refresh, 60));

  refresh();
}

/**
 * Shows each "contact us" message whose line has reached the cap. On the product
 * page the test includes what is already in the cart for that variant, since the
 * PDP selector means "how many MORE to add".
 */
function refreshQtyLimitMessages() {
  const messages = document.querySelectorAll('[data-vivana-qty-limit]');
  messages.forEach((msg) => {
    const cap = Number(msg.getAttribute('data-vivana-qty-cap')) || Number(CONFIG.maxQuantity) || 0;
    const wrapper = msg.closest('.quantity-selector-wrapper');
    const input = wrapper?.querySelector('input[type="number"]');
    if (!cap || !input) {
      msg.hidden = true;
      return;
    }
    const value = parseInt(input.value, 10) || 0;
    const isCartLine = !!input.closest('cart-quantity-selector-component');
    const alreadyInCart = isCartLine ? 0 : parseInt(input.getAttribute('data-cart-quantity'), 10) || 0;
    msg.hidden = value + alreadyInCart < cap;
  });
}

/* ─────────────────────────── PDP plan notice ──────────────────────── */

function bindPlanNotice() {
  if (!CONFIG.features.showDoiNoticeOnPDP) return;
  if (!document.querySelector(CONFIG.selectors.sellingPlanField)) return;

  const handler = (event) => {
    const field = event.target.closest(CONFIG.selectors.sellingPlanField);
    if (!field) return;
    updateNotice(readPlanFromField(field));
  };

  document.addEventListener('change', handler);
  document.addEventListener('input', handler);

  // Reflect any pre-selected plan on load (Appstle may render async).
  const tryInitial = () => {
    const field = document.querySelector(CONFIG.selectors.sellingPlanField);
    if (field) updateNotice(readPlanFromField(field));
  };
  tryInitial();
  const observer = new MutationObserver(() => {
    if (document.querySelector(CONFIG.selectors.sellingPlanField)) {
      tryInitial();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

function readPlanFromField(field) {
  const value = field.value;
  let name = '';
  if (field.tagName === 'SELECT' && field.selectedOptions?.length) {
    name = field.selectedOptions[0].textContent || '';
  }
  return { id: value, name };
}

/**
 * True only when this PDP has a doypack/refill variant the reconciler could
 * actually add. The notice must never promise refills that won't ship — e.g. a
 * self-contained bundle product (Balance + Subscription) that has no doypack
 * variant. `buy-buttons.liquid` resolves this server-side (product metafield, or
 * a Doypack/Refill-titled variant) into the form's `data-vivana-doi-variant-id`,
 * so an empty attribute means "no separate refills" → suppress the notice.
 */
function hasResolvableDoiVariant() {
  const form = document.querySelector(CONFIG.selectors.productForm);
  const attr = form?.dataset?.vivanaDoiVariantId;
  const value = attr == null ? '' : String(attr).trim();
  return value !== '' && value !== '0';
}

function updateNotice({ id, name }) {
  const tier = PlanClassifier.getTier(id, name);
  const show = PlanClassifier.requiresDoiPacks(tier) && hasResolvableDoiVariant();

  // For products that ship no separate doypacks, never create the placeholder.
  if (!show) {
    const existing = document.querySelector(CONFIG.selectors.doiNotice);
    if (existing) {
      existing.innerHTML = '';
      existing.hidden = true;
    }
    return;
  }

  const notice = ensureNoticeElement();
  if (!notice) return;

  const qty = CONFIG.doiPackQuantityMap[tier];
  notice.innerHTML =
    '<div class="vivana-plan-notice vivana-plan-notice--active">' +
    '<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
    '<path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm3.71 6.29l-4 4a1 1 0 01-1.42 0l-2-2a1 1 0 011.42-1.42L7 8.17l3.29-3.3a1 1 0 011.42 1.42z" fill="currentColor"/>' +
    '</svg>' +
    '<span>First shipment includes your Miron jar + ' +
    qty +
    ' refill doypack' +
    (qty === 1 ? '' : 's') +
    '</span></div>';
  notice.hidden = false;
}

/** Returns the notice container, creating one next to the plan field if needed. */
function ensureNoticeElement() {
  let notice = document.querySelector(CONFIG.selectors.doiNotice);
  if (notice) return notice;

  const field = document.querySelector(CONFIG.selectors.sellingPlanField);
  if (!field) return null;

  notice = document.createElement('div');
  notice.setAttribute('data-vivana-doi-notice', '');
  notice.setAttribute('aria-live', 'polite');
  notice.hidden = true;

  const anchor = field.closest('.appstle_subscription_wrapper, .appstle-wrapper, form') || field;
  anchor.appendChild(notice);
  return notice;
}

// Expose for debugging / manual triggering.
window.__VivanaSubscriptionCart = { reconcile, PlanClassifier, DoiVariantResolver, BundleResolver, CONFIG };

/* ───────────────────────────── Kickoff ─────────────────────────────── */
// Runs last so every const/let singleton above is initialized before start()
// (called synchronously by init() when the DOM is already parsed) touches them.
if (!CONFIG) {
  console.error('[Vivana] vivana-doi-config.js must load before vivana-subscription-cart.js');
} else if (CONFIG.features?.enabled !== false) {
  installAddInterceptor();
  init();
}
